import React, { useState } from 'react';
import { Popover, Button, DatePicker } from '@shopify/polaris';

// Reusable internals exported so the AdvancedFilters panel can render the
// same date range UI inline (without its own Popover wrapper).
const today = () => new Date().toISOString().split('T')[0];

function daysAgo(n) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().split('T')[0];
}

const ymdToDate = (s) => {
  if (!s) return null;
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
};

const dateToYmd = (d) => {
  if (!d) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const PRESETS = [
  { label: 'Today',   from: today(),     to: today() },
  { label: '7 days',  from: daysAgo(7),  to: today() },
  { label: '30 days', from: daysAgo(30), to: today() },
  { label: '60 days', from: daysAgo(60), to: today() },
];

/**
 * DateRangeBody — calendar + preset chips. No popover wrapper — caller decides
 * whether to wrap (column-header pill does; AdvancedFilters renders inline).
 *
 * Props (controlled): from, to (YYYY-MM-DD strings), onChange ({from,to}), onClear.
 */
export function DateRangeBody({ from, to, onChange, onClear }) {
  const startDate = ymdToDate(from);
  const initialMonth = startDate || new Date();
  const [month, setMonth] = useState(initialMonth.getMonth());
  const [year, setYear]   = useState(initialMonth.getFullYear());

  const matchedPreset = (() => {
    if (!from || !to) return null;
    const hit = PRESETS.find(p => p.from === from && p.to === to);
    return hit?.label || null;
  })();

  const applyPreset = (p) => {
    onChange({ from: p.from, to: p.to });
    const d = ymdToDate(p.from);
    if (d) { setMonth(d.getMonth()); setYear(d.getFullYear()); }
  };

  const handleCalendarChange = ({ start, end }) => {
    onChange({ from: dateToYmd(start), to: dateToYmd(end) });
  };

  const selected = (from && to)
    ? { start: ymdToDate(from), end: ymdToDate(to) }
    : (from ? { start: ymdToDate(from), end: ymdToDate(from) } : null);

  return (
    <div className="ots-daterange-body">
      <div className="ots-date-presets">
        {PRESETS.map(p => (
          <button
            key={p.label}
            type="button"
            className={`ots-date-preset-btn${matchedPreset === p.label ? ' ots-date-preset-btn--active' : ''}`}
            onClick={() => applyPreset(p)}
          >
            {p.label}
          </button>
        ))}
      </div>
      <div className="ots-date-picker-wrap">
        <DatePicker
          month={month}
          year={year}
          onChange={handleCalendarChange}
          onMonthChange={(m, y) => { setMonth(m); setYear(y); }}
          selected={selected}
          allowRange
        />
      </div>
      <div className="ots-date-readout">
        <span>{from || '—'}</span>
        <span style={{ margin: '0 8px', color: '#6d7175' }}>→</span>
        <span>{to || '—'}</span>
        {(from || to) && (
          <button
            type="button"
            className="ots-date-clear-link"
            onClick={() => onClear && onClear()}
          >
            Clear
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * DateRangeFilter — column-header date-range filter popover. Wraps DateRangeBody.
 * Props mirror the original API (from/to + Apply/Clear actions).
 */
export default function DateRangeFilter({
  activator,
  dateFrom = '',
  dateTo = '',
  onChange,
  onClear,
  active,
  onClose,
}) {
  // Local pending state — Apply commits to parent; Clear/onClose discards.
  const [localFrom, setLocalFrom] = useState(dateFrom);
  const [localTo, setLocalTo]     = useState(dateTo);

  React.useEffect(() => {
    setLocalFrom(dateFrom);
    setLocalTo(dateTo);
  }, [dateFrom, dateTo]);

  const handleApply = () => {
    onChange && onChange({ from: localFrom, to: localTo });
    onClose && onClose();
  };

  const handleClear = () => {
    setLocalFrom('');
    setLocalTo('');
    onClear && onClear();
    onClose && onClose();
  };

  const handleClose = () => {
    setLocalFrom(dateFrom);
    setLocalTo(dateTo);
    onClose && onClose();
  };

  return (
    <Popover
      active={active}
      activator={typeof activator === 'function' ? activator(active) : activator}
      onClose={handleClose}
      preferredAlignment="left"
      preferredPosition="below"
    >
      <div className="ots-filter-popover" style={{ minWidth: 300 }}>
        <DateRangeBody
          from={localFrom}
          to={localTo}
          onChange={({ from, to }) => { setLocalFrom(from); setLocalTo(to); }}
          onClear={() => { setLocalFrom(''); setLocalTo(''); }}
        />
        <div className="ots-filter-actions">
          <Button size="slim" variant="primary" onClick={handleApply}>
            Apply
          </Button>
          <Button size="slim" onClick={handleClear}>
            Clear
          </Button>
        </div>
      </div>
    </Popover>
  );
}
